import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  FormLabelDirective,
  FormSelectComponent,
  ResponsiveModalComponent,
  ResponsiveModalComponent as ResponsiveModalComponent_1,
  SvgComponent,
} from '@app/base-components';
import { CustomSiteContext } from '@app/services';
import {
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { BaseSite, BaseSiteService, I18nModule, TranslationService, WindowRef } from 'haba-core';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

interface Locale {
  code: string;
  name: string;
}

@Component({
  selector: 'app-country-popup',
  templateUrl: './country-popup.component.html',
  styleUrls: ['./country-popup.component.scss'],
  imports: [
    NgIf,
    ResponsiveModalComponent_1,
    ReactiveFormsModule,
    FormSelectComponent,
    FormLabelDirective,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgTemplateOutlet,
    NgOptionTemplateDirective,
    SvgComponent,
    AsyncPipe,
    I18nModule,
  ],
})
export class CountryPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('countryPopupModal') countryPopupModal: ResponsiveModalComponent;

  public countries$: Observable<Locale[]>;
  public languages$: Observable<Locale[]>;
  public localeForm: FormGroup;
  public initialCountry: string;
  public initialLanguage: string;
  public pendingSubmit: boolean = false;
  private unsubscribe$ = new Subject<void>();
  private window: WindowRef['nativeWindow'];
  private client: string = 'habaPro';
  private showCountryPopup: boolean = false;

  constructor(
    private winRef: WindowRef,
    private fb: FormBuilder,
    private translation: TranslationService,
    private baseSiteService: BaseSiteService,
    private cdr: ChangeDetectorRef,
    private siteContext: CustomSiteContext
  ) {
    this.localeForm = this.fb.group({
      country: [null],
      language: [null],
    });
    this.window = this.winRef.nativeWindow;
  }

  ngOnInit() {
    if (!this.winRef.isBrowser()) return;

    const countryPopupActivatedCookie = this.getCookie('countryPopupActivated');

    if (!countryPopupActivatedCookie) {
      this.siteContext
        .getActive()
        .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
        .subscribe((zone) => {
          this.initData(zone);
          this.countries$ = this.getCountries();
          this.languages$ = this.getLanguages();
        });

      this.country.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
        this.languages$ = this.getLanguages();
      });
    }
  }

  ngAfterViewInit() {
    if (!this.winRef.isBrowser() || !this.showCountryPopup) return;
    this.countryPopupModal.open();
    this.countryPopupModal.closed.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.setCookie('countryPopupActivated', 'true', 356);
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(undefined);
    this.unsubscribe$.complete();
  }

  closeModal() {
    this.countryPopupModal.close(true);
  }

  onSubmit(): void {
    const countryCode = this.country.value.code;
    const languageCode = this.language.value.code;
    if (this.initialCountry === countryCode && this.initialLanguage === languageCode) {
      this.closeModal();
      return;
    }
    this.setCookie('countryPopupActivated', 'true', 356);
    this.pendingSubmit = true;
    this.window.location.href = this.window.location.href.replace(
      `/${this.initialLanguage.toLowerCase()}-${this.initialCountry.toLowerCase()}/`,
      `/${languageCode}-${countryCode?.toLowerCase()}/`
    );
  }

  getCountryName(code: string): string {
    return this.localeTranslate(`countries.${code}`);
  }

  private initData(zone: string): void {
    const [language, country] = zone.split('-');

    this.initialCountry = country.toUpperCase();
    this.initialLanguage = language.toLowerCase();
    const myraCountry = this.getCookie('x-myra-geo-country-code')?.toUpperCase() || 'DE';
    this.showCountryPopup = this.initialCountry !== myraCountry;
    this.country.patchValue({
      code: this.initialCountry,
      name: this.localeTranslate(`countries.${this.initialCountry}`),
    });
    this.language.patchValue({
      code: this.initialLanguage,
      name: this.localeTranslate(`languages.${this.initialLanguage}`),
    });
    this.cdr.detectChanges();
  }

  private getCountries(): Observable<Locale[]> {
    return this.baseSiteService.getAll().pipe(
      map((baseStores) =>
        baseStores
          .filter((baseStore) => baseStore.baseStore && baseStore.uid.includes(this.client))
          .map((baseStore) => {
            const code = baseStore.uid.slice(-2);
            return { code, name: this.localeTranslate(`countries.${code}`) };
          })
      )
    );
  }

  private getLanguages(): Observable<Locale[]> {
    const currentBasestoreUid = `${this.client}${this.country.value.code}`;
    return this.baseSiteService.get(currentBasestoreUid).pipe(
      map((baseStore: BaseSite) => {
        const languages = baseStore?.baseStore?.languages?.map((language) => {
          const code = language.isocode;
          return { code, name: this.localeTranslate(`languages.${code}`) };
        });
        const checkedLanguageIsInLanguages = languages?.some(
          (language) => language.code === this.language.value.code
        );
        if (!checkedLanguageIsInLanguages && languages?.[0]) {
          this.language.patchValue(languages[0]);
        }
        languages?.length === 1 ? this.language.disable() : this.language.enable();
        return languages;
      })
    );
  }

  private get country(): AbstractControl {
    return this.localeForm?.controls['country'];
  }

  private get language(): AbstractControl {
    return this.localeForm?.controls['language'];
  }

  private localeTranslate(key: string): string {
    let translation: string;
    this.translation
      .translate(key)
      .subscribe((name) => (translation = name))
      .unsubscribe();
    return translation;
  }

  private setCookie(name: string, value: string, expireDays: number, path: string = '') {
    const d: Date = new Date();
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
    const expires: string = `expires=${d.toUTCString()}`;
    const cpath: string = path ? `; path=${path}` : '';
    this.winRef.document.cookie = `${name}=${value}; ${expires}${cpath}`;
  }

  private getCookie(name: string) {
    const ca: Array<string> = this.winRef.document.cookie.split(';');
    const caLen: number = ca.length;
    const cookieName = `${name}=`;
    let c: string;

    for (let i: number = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) == 0) {
        return c.substring(cookieName.length, c.length);
      }
    }
    return undefined;
  }
}
